import {
  Route,
  Routes,
  BrowserRouter as Router,
  Navigate,
} from "react-router-dom";
//
import "./App.css";
import { Rent } from "./Pages/Rent/Rent";
import { MainLayouts } from "./Layouts/MainLayouts";
import { Details } from "./Pages/Rent/Details";
import { ConfirmWrapper } from "./Pages/Rent/ConfirmWrapper";
import { ConnectModal, TransactionModal } from "./Components/Modal/Modal";
import { DashboardLayouts } from "./Layouts/DashboardLayouts";
import { HostDashboard } from "./Pages/Dashboard/HostDashboard/Dashboard";
import { Profile } from "./Pages/Dashboard/Profile/Profile";
import MintMain from "./Pages/Dashboard/HostDashboard/Mint/MintMain";
import { Inbox } from "./Pages/Dashboard/Inbox/Inbox";
import { HostNFTs } from "./Pages/Dashboard/HostNFTs/HostNFTs";
import { Reservations } from "./Pages/Dashboard/HostReservations/Reservations";
import { ReserveDetails } from "./Pages/Dashboard/HostReservations/ReserveDetails";
import { TravelerDashboard } from "./Pages/Dashboard/TravelerDashboard/Dashboard";
import { HostCalendar } from "./Pages/Dashboard/HostCalendar/Calendar";
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-tooltip/dist/react-tooltip.css";
import "react-loading-skeleton/dist/skeleton.css";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import {
  setMessageToSend,
  setReceivedMessage,
  setTokenUpdate,
} from "./ReduxSlices/CommunicationSlice";
import { Mainnet } from "@nibiruchain/nibijs";
import { EditNFT } from "./Pages/Dashboard/HostNFTs/EditNFT";
import {
  updateToken,
  getTokens,
  normalizePropertyForStore,
  isSameWalletAddress,
} from "./Components/functions/Functions";
import { queryContract } from "./Components/functions/Contract";
import { updateNFTStore } from "./ReduxSlices/NFTSlice";
import { setDiffToUTC } from "./ReduxSlices/TimeSlice";
import { setReservationFee } from "./ReduxSlices/ReservationSlice";
import { Trips } from "./Pages/Dashboard/TravelerTrips/Trips";
import { Transactions } from "./Pages/Dashboard/Transaction/Transactions";
import { setEventToEmit, addItem, setLastViewedAt } from "./ReduxSlices/NotificationSlice";
import { api } from "./Components/functions/Api";
import { setCurrentProfile } from "./Layouts/DashboardLayouts";
import { Tooltip } from "react-tooltip";
import { useWalletReconnection } from "./hooks/useWalletReconnection";
import { WalletReconnectionIndicator } from "./Components/WalletReconnectionIndicator";
import propertyCacheService from "./Services/PropertyCacheService";
import CodedEstateLanding from "./Pages/OnBoarding/OnBoarding";
import Component from "./Pages/OnBoarding/Completion";
import Privacy from "./Pages/OnBoarding/Privacy";
import Terms from "./Pages/OnBoarding/Terms";
import About from "./Pages/OnBoarding/About";
import { InstructionRent } from "./Pages/OnBoarding/InstructionRental";
import { StaticTravelerDashboard } from "./Pages/OnBoarding/Dashboard/TravelerDashboard";
import { StaticHostDashboard1 } from "./Pages/OnBoarding/HostDashboard";
import { OnboardingProvider } from "./Pages/OnBoarding/OnBoarding1";
import { ChatRedirect } from "./Pages/Dashboard/Inbox/ChatRedirect";
import { upsertMintJob } from "./ReduxSlices/MintSlice";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CalendarSyncPage from "./Pages/Dashboard/CalendarSync/CalendarSyncPage";

const toPositiveNumber = (value, fallback) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const HOST_NFT_PAGE_SIZE = toPositiveNumber(
  process.env.REACT_APP_HOST_NFT_PAGE_SIZE,
  40
);
const HOST_NFT_SCROLL_THRESHOLD = toPositiveNumber(
  process.env.REACT_APP_HOST_NFT_SCROLL_THRESHOLD,
  200
);

function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000, // 30 seconds of fresh data for dashboard views
            gcTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            retry: 1,
          },
        },
      })
  );
  const account = useSelector((state) => state.auth.account);
  const tokenUpdate = useSelector((state) => state.communication.tokenUpdate);
  const messageToSend = useSelector(
    (state) => state.communication.messageToSend
  );
  const eventToEmit = useSelector((state) => state.notification.eventToEmit);

  const [socket, setSocket] = useState(null);
  const dispatch = useDispatch();

  // Initialize wallet reconnection
  useWalletReconnection();
  const mainnet = Mainnet();

  const refreshPropertyFromBackend = useCallback(
    async (tokenId) => {
      if (!tokenId) return null;
      try {
        const property = await api("property/getProperty", {
          token_id: tokenId,
        });
        if (property?.token_id) {
          // 🔥 FIX: Validate metaData exists before storing in Redux
          if (!property.metaData || typeof property.metaData !== 'object') {
            console.warn("⚠️ App: Invalid metaData received from backend:", {
              tokenId,
              hasMetaData: !!property.metaData,
              metaDataType: typeof property.metaData
            });
            return null;
          }
          
          const payload = normalizePropertyForStore(property);
          if (payload) {
            dispatch(updateNFTStore(payload));
          }
        }
        return property;
      } catch (error) {
        console.warn(
          "Failed to refresh property from backend:",
          tokenId,
          error
        );
        return null;
      }
    },
    [dispatch]
  );

  const getCurrentUTCTime = async () => {
    try {
      const utcTimeStamp = await (
        await fetch("https://worldtimeapi.org/api/timezone/Etc/UTC")
      ).json();
      return utcTimeStamp?.utc_datetime;
    } catch (error) {
      console.log(error);
      return new Date();
    }
  };

  const diffToUTC = async () => {
    const utc = new Date(await getCurrentUTCTime());
    const diffSec =
      Math.floor(utc.getTime() / 1000) -
      Math.floor(new Date().getTime() / 1000);
    dispatch(setDiffToUTC(diffSec));
  };

  // 🚀 OPTIMIZED: Removed RPC-based updateNFTs - getTokens already handles this via backend API
  // This function was making unnecessary RPC calls for every token
  // Now we rely on getTokens which uses backend API first, then optionally syncs from blockchain
  const updateNFTs = async (account) => {
    // No-op: getTokens already handles property loading via backend API
    // Only sync from blockchain if explicitly needed (handled by getTokens)
    console.log("🚀 Skipping updateNFTs - using optimized getTokens instead");
  };

  const setNotificationContent = async () => {
    const res = await api("user/inbox", {
      account: account,
    });
    for (let i = 0; i < res.length; i++) {
      if (res[i].unreadMessages) {
        const r = await api("chat/getChattingInfo", {
          id: res[i].chatId,
          reader: account,
        });

        dispatch(
          addItem({
            type: "message",
            data: {
              id: res[i].chatId,
              message: {
                // 🚀 PHASE 7: Fix notification sender - use actual sender from latest message, not receiver
                sender: r.latestMessageSender || r.receiver, // Use latestMessageSender if available, fallback to receiver
                generatedTime: r.latestMessageTime,
                text: null,
              },
              nftId: r.nftId,
              mode: r.mode,
            },
          })
        );
      }
    }
    const events = await api("user/unreadTxLogs", {
      account: account,
    });
    for (let i = 0; i < events.length; i++) {
      dispatch(
        addItem({
          type: "event",
          data: events[i],
        })
      );
    }
  };

  useEffect(() => {
    if (account) {
      updateNFTs(account);
      getTokens(account, mainnet.endptTm, dispatch);
      setNotificationContent();
      // 🚀 GLOBAL NOTIFICATION VIEWED STATE: Fetch lastViewedAt from server
      const fetchLastViewedAt = async () => {
        try {
          const response = await api("user/getNotificationLastViewed", {
            account: account,
          });
          if (response?.success && response.lastViewedAt !== undefined) {
            dispatch(setLastViewedAt(response.lastViewedAt));
          }
        } catch (error) {
          console.warn("Failed to fetch notification last viewed:", error);
        }
      };
      fetchLastViewedAt();
    }
  }, [account]);

  useEffect(() => {
    if (socket) socket.disconnect();

    const tempSocket = io(process.env.REACT_APP_BACKEND_SERVER, {
      withCredentials: true,
      transports: ["websocket", "polling"],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      extraHeaders: {
        "Access-Control-Allow-Origin": "*",
      },
    });

    const onPropertyPending = (payload = {}) => {
      const propertyId = payload?.propertyId;
      dispatch(
        upsertMintJob({
          ...payload,
          propertyId,
          sync_status: payload?.sync_status || "pending",
        })
      );

      if (propertyId) {
        toast.info(`Mint queued for property ${propertyId}`);
      } else {
        toast.info("Mint request queued.");
      }
    };

    const onPropertyConfirmed = async (payload = {}) => {
      const propertyId = payload?.propertyId;
      dispatch(
        upsertMintJob({
          ...payload,
          propertyId,
          sync_status: payload?.sync_status || "confirmed",
        })
      );

      if (propertyId) {
        toast.success(`Mint confirmed for property ${propertyId}!`);
        await refreshPropertyFromBackend(propertyId);
        // So HostNFTs (and any host list) refetches and keeps the new mint visible
        window.dispatchEvent(new CustomEvent("property:confirmed", { detail: payload }));
      } else {
        toast.success("Mint confirmed!");
      }
    };

    const onPropertyFailed = (payload = {}) => {
      const propertyId = payload?.propertyId;
      dispatch(
        upsertMintJob({
          ...payload,
          propertyId,
          sync_status: "failed",
        })
      );

      const failureMessage =
        payload?.error ||
        payload?.failureReason ||
        "Mint failed. Please try again.";
      if (propertyId) {
        toast.error(`Mint failed for property ${propertyId}: ${failureMessage}`);
      } else {
        toast.error(failureMessage);
      }
    };

    tempSocket.on("connect", () => {
      tempSocket.emit("connectedAccount", account);
      console.log("connected to the server...");
    });
    tempSocket.on("disconnect", () => {
      console.log("disconnected from server, Socket.io will handle reconnection automatically");
      // Removed manual reconnection - Socket.io handles this automatically
    });
    tempSocket.on("tokenUpdated", (data) => {
      if (data.sender === account)
        updateToken(data, mainnet.endptTm, dispatch, true);
      else updateToken(data, mainnet.endptTm, dispatch);
    });
    tempSocket.on("event", (res) => {
      dispatch(
        addItem({
          type: "event",
          data: res,
        })
      );
      
      // 🚀 REAL-TIME TRANSACTIONS: Emit custom event for instant transaction updates
      if (res?.txHash && res?.action && res?.token_id) {
        console.log('📋 Transaction event received, notifying components:', res.action);
        window.dispatchEvent(new CustomEvent('transaction:added', {
          detail: res
        }));
      }
    });
    
    // 🚀 REAL-TIME TRANSACTIONS: Dedicated transaction socket event for instant updates
    tempSocket.on("transaction:added", (data) => {
      console.log('📋 Direct transaction:added event received:', data.action);
      window.dispatchEvent(new CustomEvent('transaction:added', {
        detail: data
      }));
    });
    tempSocket.on("property:pending", onPropertyPending);
    tempSocket.on("property:confirmed", onPropertyConfirmed);
    tempSocket.on("property:failed", onPropertyFailed);
    
    // 🚀 INSTANT DASHBOARDS: Fine-grained reservation socket events for real-time updates
    tempSocket.on("reservation:pending", (data) => {
      console.log("📋 Reservation pending:", data);
      // Emit custom event for components to listen
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'pending' }
      }));
      window.dispatchEvent(new CustomEvent('reservation:pending', {
        detail: { ...data, status: 'pending' }
      }));
    });
    
    tempSocket.on("reservation:pending.v2", (data) => {
      console.log("📋 Reservation pending (v2):", data);
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'pending' }
      }));
      window.dispatchEvent(new CustomEvent('reservation:pending.v2', {
        detail: { ...data, status: 'pending' }
      }));
    });
    
    tempSocket.on("reservation:confirmed", (data) => {
      console.log("✅ Reservation confirmed:", data);
      // Emit custom event for components to listen
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'confirmed' }
      }));
      window.dispatchEvent(new CustomEvent('reservation:confirmed', {
        detail: { ...data, status: 'confirmed' }
      }));
      // Also emit for backward compatibility
      window.dispatchEvent(new CustomEvent('reservationConfirmed', {
        detail: data
      }));
    });
    
    tempSocket.on("reservation:failed", (data) => {
      console.log("❌ Reservation failed:", data);
      // Emit custom event for components to listen
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'failed' }
      }));
      window.dispatchEvent(new CustomEvent('reservation:failed', {
        detail: { ...data, status: 'failed' }
      }));
    });
    
    tempSocket.on("reservation:updated", (data) => {
      console.log("🔄 Reservation updated:", data);
      // Emit custom event for components to listen
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'updated' }
      }));
      window.dispatchEvent(new CustomEvent('reservation:updated', {
        detail: { ...data, status: 'updated' }
      }));
      // Also emit for backward compatibility
      window.dispatchEvent(new CustomEvent('reservationUpdated', {
        detail: data
      }));
    });
    
    // 🚀 INSTANT DASHBOARDS: Additional fine-grained events for reservation actions
    tempSocket.on("reservation:approved", async (data) => {
      console.log("✅ Reservation approved:", data);
      
      // 🚀 PHASE 6: Immediately refresh property in Redux for instant UI update
      if (data?.token_id) {
        try {
          const property = await refreshPropertyFromBackend(data.token_id);
          if (property) {
            console.log("✅ Property refreshed in Redux after approval");
          }
        } catch (error) {
          console.warn("Failed to refresh property after approval:", error);
        }
      }
      
      window.dispatchEvent(new CustomEvent('reservation:approved', {
        detail: { ...data, status: 'approved' }
      }));
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'approved' }
      }));
      // Backward compatibility
      window.dispatchEvent(new CustomEvent('reservationApproved', {
        detail: data
      }));
    });

    tempSocket.on("reservation:approved.v2", async (data) => {
      console.log("✅ Reservation approved (v2):", data);
      if (data?.token_id) {
        try {
          const property = await refreshPropertyFromBackend(data.token_id);
          if (property) console.log("✅ Property refreshed in Redux after approval (v2)");
        } catch (error) {
          console.warn("Failed to refresh property after approval (v2):", error);
        }
      }
      window.dispatchEvent(new CustomEvent('reservation:approved', { detail: { ...data, status: 'approved' } }));
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', { detail: { ...data, status: 'approved' } }));
      window.dispatchEvent(new CustomEvent('reservationApproved', { detail: data }));
    });
    
    tempSocket.on("reservation:cancelled", async (data) => {
      console.log("❌ Reservation cancelled:", data);
      
      // 🚀 PHASE 6: Immediately refresh property in Redux for instant UI update
      if (data?.token_id) {
        try {
          const property = await refreshPropertyFromBackend(data.token_id);
          if (property) {
            console.log("✅ Property refreshed in Redux after cancellation");
          }
        } catch (error) {
          console.warn("Failed to refresh property after cancellation:", error);
        }
      }
      
      window.dispatchEvent(new CustomEvent('reservation:cancelled', {
        detail: { ...data, status: 'cancelled' }
      }));
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'cancelled' }
      }));
      // Backward compatibility
      window.dispatchEvent(new CustomEvent('reservationCancelled', {
        detail: data
      }));
    });

    tempSocket.on("reservation:cancelled.v2", async (data) => {
      console.log("❌ Reservation cancelled (v2):", data);
      if (data?.token_id) {
        try {
          const property = await refreshPropertyFromBackend(data.token_id);
          if (property) console.log("✅ Property refreshed in Redux after cancellation (v2)");
        } catch (error) {
          console.warn("Failed to refresh property after cancellation (v2):", error);
        }
      }
      window.dispatchEvent(new CustomEvent('reservation:cancelled', { detail: { ...data, status: 'cancelled' } }));
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', { detail: { ...data, status: 'cancelled' } }));
      window.dispatchEvent(new CustomEvent('reservationCancelled', { detail: data }));
    });
    
    tempSocket.on("reservation:rejected", async (data) => {
      console.log("❌ Reservation rejected:", data);
      
      // 🚀 PHASE 6: Immediately refresh property in Redux for instant UI update
      if (data?.token_id) {
        try {
          const property = await refreshPropertyFromBackend(data.token_id);
          if (property) {
            console.log("✅ Property refreshed in Redux after rejection");
          }
        } catch (error) {
          console.warn("Failed to refresh property after rejection:", error);
        }
      }
      
      window.dispatchEvent(new CustomEvent('reservation:rejected', {
        detail: { ...data, status: 'rejected' }
      }));
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'rejected' }
      }));
      // Backward compatibility
      window.dispatchEvent(new CustomEvent('reservationRejected', {
        detail: data
      }));
    });

    tempSocket.on("reservation:rejected.v2", async (data) => {
      console.log("❌ Reservation rejected (v2):", data);
      if (data?.token_id) {
        try {
          const property = await refreshPropertyFromBackend(data.token_id);
          if (property) console.log("✅ Property refreshed in Redux after rejection (v2)");
        } catch (error) {
          console.warn("Failed to refresh property after rejection (v2):", error);
        }
      }
      window.dispatchEvent(new CustomEvent('reservation:rejected', { detail: { ...data, status: 'rejected' } }));
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', { detail: { ...data, status: 'rejected' } }));
      window.dispatchEvent(new CustomEvent('reservationRejected', { detail: data }));
    });
    
    tempSocket.on("reservation:finalized", async (data) => {
      console.log("✅ Reservation finalized:", data);
      
      // 🚀 PHASE 6: Immediately refresh property in Redux for instant UI update
      if (data?.token_id) {
        try {
          const property = await refreshPropertyFromBackend(data.token_id);
          if (property) {
            console.log("✅ Property refreshed in Redux after finalization");
          }
        } catch (error) {
          console.warn("Failed to refresh property after finalization:", error);
        }
      }
      
      window.dispatchEvent(new CustomEvent('reservation:finalized', {
        detail: { ...data, status: 'finalized' }
      }));
      window.dispatchEvent(new CustomEvent('reservationStatusUpdate', {
        detail: { ...data, status: 'finalized' }
      }));
      // Backward compatibility
      window.dispatchEvent(new CustomEvent('reservationFinalized', {
        detail: data
      }));
    });
    
    // 🚀 INSTANT DASHBOARDS: Property list status changes
    tempSocket.on("property:listStatusChanged", (data) => {
      console.log("🔄 Property list status changed:", data);
      window.dispatchEvent(new CustomEvent('property:listStatusChanged', {
        detail: data
      }));
      if (data?.token_id) {
        refreshPropertyFromBackend(data.token_id);
      }
    });
    
    tempSocket.on("messageReceived", (res) => {
      // 🚀 PHASE 7: Handle echo messages (from sender's own devices) vs real messages
      // Backend now sends _isEcho flag for sender's own messages
      const isEcho = res._isEcho === true;
      
      // Always dispatch for UI updates (both echo and real messages)
      dispatch(setReceivedMessage(res));
      
      // Only add notification if:
      // 1. It's NOT an echo (real message from another user)
      // 2. Sender is different from current user
      if (!isEcho && res.message) {
        // Only notify when the message is from someone else (handles EVM vs nibi same key)
        if (
          res.message.sender &&
          account &&
          !isSameWalletAddress(res.message.sender, account)
        ) {
          dispatch(
            addItem({
              type: "message",
              data: res,
            })
          );
        }
      }
    });
    setSocket(tempSocket);
    return () => {
      try {
        tempSocket.off("property:pending", onPropertyPending);
        tempSocket.off("property:confirmed", onPropertyConfirmed);
        tempSocket.off("property:failed", onPropertyFailed);
        tempSocket.off("reservation:pending");
        tempSocket.off("reservation:pending.v2");
        tempSocket.off("reservation:confirmed");
        tempSocket.off("reservation:failed");
        tempSocket.off("reservation:updated");
        tempSocket.off("reservation:approved");
        tempSocket.off("reservation:approved.v2");
        tempSocket.off("reservation:cancelled");
        tempSocket.off("reservation:cancelled.v2");
        tempSocket.off("reservation:rejected");
        tempSocket.off("reservation:rejected.v2");
        tempSocket.off("reservation:finalized");
        tempSocket.off("property:listStatusChanged");
        tempSocket.off("transaction:added");
        tempSocket.disconnect();
      } catch (e) {}
    };
  }, [account, refreshPropertyFromBackend]);

  useEffect(() => {
    if (tokenUpdate) {
      socket.emit("tokenUpdated", tokenUpdate);
      dispatch(setTokenUpdate(null));
    }
  }, [tokenUpdate]);

  useEffect(() => {
    if (messageToSend && socket && socket.connected) {
      console.log("📤 Sending message via socket:", messageToSend);

      let acked = false;
      const payload = messageToSend;

      // Fallback timer - if no ack in 800ms, use HTTP
      const fallbackTimer = setTimeout(async () => {
        if (!acked) {
          console.log("⏱️ Socket timeout, falling back to HTTP");
          try {
            const res = await api("chat/sendMessage", payload);
            if (res.success) {
              console.log("✅ HTTP fallback successful");
            } else {
              toast.error("Failed to send message");
            }
          } catch (error) {
            console.error("❌ HTTP fallback failed:", error);
            toast.error("Failed to send message");
          }
        }
      }, 800);
//
      // Send via socket with acknowledgment
      socket.emit("sendMessage", payload, (response) => {
        acked = true;
        clearTimeout(fallbackTimer);

        if (response?.success) {
          console.log("✅ Socket message acknowledged:", response);
          if (response.isCreat && response.id) {
            console.log("🆕 New chat created:", response.id);
          }
        } else {
          console.error("❌ Socket ack failed:", response?.error);
          // Fallback to HTTP immediately
          api("chat/sendMessage", payload)
            .then((res) => {
              if (!res.success) toast.error("Failed to send message");
            })
            .catch(() => toast.error("Failed to send message"));
        }
      });

      dispatch(setMessageToSend(null));
    } else if (messageToSend && (!socket || !socket.connected)) {
      // Socket not ready, use HTTP immediately
      console.log("📤 Socket not connected, using HTTP");
      api("chat/sendMessage", messageToSend)
        .then((res) => {
          if (!res.success) toast.error("Failed to send message");
        })
        .catch(() => toast.error("Failed to send message"));
      dispatch(setMessageToSend(null));
    }
  }, [messageToSend, socket, dispatch]);

  useEffect(() => {
    if (eventToEmit) {
      socket.emit("event", eventToEmit);
      dispatch(setEventToEmit(null));
    }
  }, [eventToEmit]);

  useEffect(() => {
    diffToUTC();
    
    // Initialize property cache monitoring - DISABLED: No auto-refresh
    // propertyCacheService.startCacheMonitoring();
    
    // Cleanup on unmount
    return () => {
      propertyCacheService.cleanup();
    };
  }, []);

  // Load platform fee once so Details/Confirm show Coded Estate service fee (contract or env fallback)
  useEffect(() => {
    const fallbackFeeBps =
      Number(process.env.REACT_APP_PLATFORM_FEE_BPS) || 300;
    const run = async () => {
      try {
        const res = await queryContract(
          process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          { get_fee: {} },
          mainnet.endptTm
        );
        const feeBps =
          typeof res === "object" && res !== null && "fee" in res
            ? Number(res.fee)
            : Number(res);
        const value = Number.isFinite(feeBps) ? feeBps : fallbackFeeBps;
        dispatch(setReservationFee(value));
      } catch (_) {
        dispatch(setReservationFee(fallbackFeeBps));
      }
    };
    run();
  }, [dispatch]);

  useEffect(() => {
    if (account) setCurrentProfile(account, dispatch);
  }, [account]);

  return (
    <QueryClientProvider client={queryClient}>
      <div
        style={{ fontFamily: "General Sans" }}
        className="w-[100vw] h-screen bg-[#fafafa] font-medium"
      >
        <WalletReconnectionIndicator />
        <Router>
          <OnboardingProvider>
        <Routes>
          <Route path="*" element={<MainLayouts />}>
            <Route path="" element={<Navigate to="/rent/onboarding" />} />
            
            {/* Deep link route for email notifications */}
            <Route path="chat/:chatId" element={<ChatRedirect />} />
            
            <Route path="rent" element={<Navigate to="rent/all" />} />
            <Route path="rent/*" element={<Rent />} />
            <Route path="rent/short/:id/*" element={<Details />} />
            <Route path="rent/all/:id/*" element={<Details />} />
            <Route path="rent/long/:id/*" element={<Details />} />
            <Route path="rent/onboarding" element={<CodedEstateLanding />} />
            <Route path="rent/onboarding/Completion" element={<Component />} />
            <Route path="rent/onboarding/privacy" element={<Privacy />} />
            <Route path="rent/onboarding/terms" element={<Terms />} />
            <Route path="rent/onboarding/about" element={<About />} />
            <Route path="onboarding/steps" element={<InstructionRent />} />
            <Route path="onboarding/host" element={<HostDashboard />} />
            <Route path="onboarding/host1" element={<StaticHostDashboard1 />} />
            <Route path="onboarding/traveller" element={<StaticTravelerDashboard />} />

            <Route path="rent/short/:id/Confirm" element={<ConfirmWrapper />} />
            <Route path="rent/all/:id/Confirm" element={<ConfirmWrapper />} />
            <Route path="rent/long/:id/Confirm" element={<ConfirmWrapper />} />

            <Route path="dashboard/*" element={<DashboardLayouts />}>
              <Route path="profile" element={<Profile />} />
              <Route path="profile/:address" element={<Profile />} />
              <Route path="host/dashboard" element={<HostDashboard />} />
              <Route path="host/inbox" element={<Inbox mode="host" />} />
              <Route path="host/reservations" element={<Reservations />} />
              <Route path="traveler/trips" element={<Trips />} />
              <Route
                path="host/reservations/:id"
                element={<ReserveDetails />}
              />
              <Route path="traveler/trips/:id" element={<ReserveDetails />} />
              <Route path="host/nfts/:id/edit" element={<EditNFT />} />
              <Route path="host/nfts/:id" element={<Details />} />
              <Route
                path="host/nfts"
                element={
                  <HostNFTs
                    pageSize={HOST_NFT_PAGE_SIZE}
                    scrollThreshold={HOST_NFT_SCROLL_THRESHOLD}
                  />
                }
              />
              <Route
                path="traveler/inbox"
                element={<Inbox mode="traveler" />}
              />
              <Route
                path="traveler/dashboard"
                element={<TravelerDashboard />}
              />
              <Route
                path="traveler/transactions"
                element={<Transactions mode="traveler" />}
              />
              <Route
                path="host/transactions"
                element={<Transactions mode="host" />}
              />
              <Route path="host/mint" element={<MintMain />} />
              <Route path="host/calendar" element={<HostCalendar />} />
              <Route path="host/calendar-sync/:propertyId" element={<CalendarSyncPage />} />
            </Route>
          </Route>
        </Routes>
          </OnboardingProvider>
          <TransactionModal />
          <ConnectModal />
        </Router>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss
          draggable
          theme="light"
        />
        <Tooltip
          id="my-tooltip"
          content="Coming soon"
          style={{ borderRadius: "4px", padding: "6px" }}
        />
        <Tooltip
          id="addToWishlist"
          content="Add to Wishlist"
          style={{ borderRadius: "4px", fontSize: "12px", padding: "6px" }}
        />
        <Tooltip
          id="removeFromWishlist"
          content="Remove from Wishlist"
          style={{ borderRadius: "4px", fontSize: "12px", padding: "6px" }}
        />
      </div>
    </QueryClientProvider>
  );
}

export default App;